import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Code2, Download, Eye, History, Link2, Loader2, MessageSquare, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Composer, uploadTo } from "@/components/studio/chat/Composer";
import { Message, StepsCard } from "@/components/studio/chat/Messages";
import { CodePane } from "@/components/studio/chat/CodePane";
import { QuoteBar } from "@/components/studio/chat/QuoteBar";
import { DevicePreview } from "@/components/studio/DevicePreview";
import { VersionsPanel } from "@/components/studio/VersionsPanel";
import { PublishPanel } from "@/components/studio/PublishPanel";
import { PublishPanel as TwentyIPublish } from "@/components/twentyi/PublishPanel";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
const useNarrow = () => {
  const [n, setN] = useState(window.innerWidth < 900);
  useEffect(() => { const f = () => setN(window.innerWidth < 900); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return n;
};

const Chat = ({ pid, chat, running, act, composerRef }) => {
  const end = useRef(null);
  const msgs = chat.messages;
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs.length, chat.job?.steps?.length, running]);
  const last = (k) => [...msgs].reverse().find((m) => m.kind === k)?.id;
  const job = chat.job;
  const ctx = {
    running, currentVersion: chat.project.current_version, latestPlanId: last("plan"), latestQuestionsId: last("questions"), hasPlan: !!chat.project.plan,
    latestErrorId: job?.state === "failed" ? last("error") : null,
    onAnswer: (text) => act.send(text, []), onApprove: act.approve, onRetry: act.retry, onRollback: act.rollback,
    onChangePlan: () => composerRef.current?.querySelector("textarea")?.focus(),
  };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div data-testid="chat-messages" className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {msgs.length === 0 && !running && <p className="text-sm text-slate-500">Describe what to build to get started.</p>}
        {msgs.map((m) => <Message key={m.id} m={m} ctx={ctx} />)}
        {running && <StepsCard live steps={job?.steps || []} cost={(job?.steps || []).reduce((s, x) => s + (x.cost || 0), 0)} testId="steps-live" />}
        <div ref={end} />
      </div>
      <div ref={composerRef} className="border-t border-white/5 p-3">
        <Composer onSend={act.send} upload={uploadTo(pid)} running={running} onStop={act.stop} disabled={running}
          placeholder={chat.project.current_version ? "Ask for a change…" : chat.project.plan ? "What should change in the plan?" : "Reply or say “just build it”…"} />
      </div>
    </div>
  );
};

const RightPane = ({ tab, setTab, chat, detail, preview, running, reload }) => {
  const pages = (detail?.versions.find((v) => v.number === chat.project.current_version)?.files || [])
    .map((f) => f.name).filter((n) => n.endsWith(".html") && n !== "_nav.html").sort((a, b) => (a === "index.html" ? -1 : b === "index.html" ? 1 : a.localeCompare(b)));
  const p = chat.project;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-1 border-b border-white/5 px-3 py-2">
        {[["preview", "Preview", Eye], ["code", "Code", Code2]].map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`right-tab-${k}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${tab === k ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}><I className="h-3.5 w-3.5" />{l}</button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {tab === "preview"
          ? <div className="h-full p-3"><DevicePreview fill url={preview} pages={pages} appId={p.app_mode && p.data_model ? p.id : null} /></div>
          : <CodePane pid={p.id} version={p.current_version} disabled={running} onSaved={reload} />}
      </div>
    </div>
  );
};

const TopBar = ({ chat, detail, quote, setQuote, running, setDrawer, setPub }) => {
  const p = chat.project;
  const download = async () => {
    try {
      const r = await api.get(`/admin/studio/projects/${p.id}/versions/${p.current_version}/zip`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-v${p.current_version}.zip`; a.click();
    } catch (e) { err(e); }
  };
  const client = detail?.clientProject;
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-white/5 bg-[#07090E] px-3">
      <Link to="/admin/studio" aria-label="Back to AI Studio" data-testid="ws-back" className="rounded-full p-2 text-slate-400 hover:bg-white/5 hover:text-white"><ArrowLeft className="h-4 w-4" /></Link>
      <div className="min-w-0 flex-1">
        <p data-testid="ws-project-name" className="truncate text-sm font-semibold text-white">{p.name}</p>
        {client ? <Link to={`/admin/projects/${client.id}`} data-testid="ws-client-link" className="flex max-w-full items-center gap-1 text-[11px] text-cyan-300/80 hover:text-cyan-200"><Link2 className="h-3 w-3 shrink-0" /><span className="truncate">{client.name}</span></Link>
          : <p className="truncate text-[11px] text-slate-500">{p.current_version ? `v${p.current_version}` : "Not built yet"}{running ? " · working…" : ""}</p>}
      </div>
      <QuoteBar pid={p.id} quote={quote} setQuote={setQuote} />
      <button onClick={() => setDrawer(true)} disabled={!detail?.versions.length} data-testid="ws-history" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-400/40 disabled:opacity-40"><History className="h-3.5 w-3.5" /><span className="hidden sm:inline">History</span></button>
      <button onClick={download} disabled={!p.current_version} aria-label="Download zip" data-testid="ws-download" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-400/40 disabled:opacity-40"><Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">Download</span></button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button disabled={!p.current_version || running} data-testid="ws-publish" className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-3 py-1.5 text-xs font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-40"><Rocket className="h-3.5 w-3.5" />Publish<ChevronDown className="h-3 w-3" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-white/10 bg-[#0D111A] text-slate-200">
          <DropdownMenuItem onSelect={() => setPub("site")} data-testid="ws-publish-site">Publish to my site</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPub("20i")} data-testid="ws-publish-20i">Publish to 20i</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

export default function ChatWorkspace() {
  const { id } = useParams();
  const [chat, setChat] = useState(null);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [quote, setQuote] = useState(null);
  const [tab, setTab] = useState("preview");
  const [mobileTab, setMobileTab] = useState("chat");
  const [drawer, setDrawer] = useState(false);
  const [pub, setPub] = useState(null);
  const narrow = useNarrow();
  const composerRef = useRef(null);
  const version = chat?.project.current_version;
  const running = chat?.job?.state === "running";

  const loadChat = useCallback(() => api.get(`/admin/studio/projects/${id}/chat`).then((r) => setChat(r.data)).catch(err), [id]);
  const loadDetail = useCallback(async () => {
    const d = (await api.get(`/admin/studio/projects/${id}`)).data;
    const cp = d.project.client_project_id ? (await api.get(`/projects/${d.project.client_project_id}`).catch(() => null))?.data : null;
    setDetail({ ...d, clientProject: cp });
  }, [id]);
  const loadQuote = useCallback(() => api.get(`/admin/studio/projects/${id}/quote`).then((r) => setQuote(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadChat(); loadDetail(); loadQuote(); }, [loadChat, loadDetail, loadQuote]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { loadChat(); loadQuote(); }, 1000);
    return () => clearInterval(t);
  }, [running, loadChat, loadQuote]);
  useEffect(() => { if (!running && chat) { loadDetail(); loadQuote(); } }, [running]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!version) { setPreview(null); return; }
    api.get(`/admin/studio/projects/${id}/preview-url`).then((r) => setPreview(r.data.url)); // fresh token per version = fresh load
  }, [id, version, detail?.versions?.length]);

  const call = async (fn) => { try { await fn(); } catch (e) { err(e); } loadChat(); };
  const act = {
    send: (text, images) => call(() => api.post(`/admin/studio/projects/${id}/chat`, { text, images })),
    approve: () => call(() => api.post(`/admin/studio/projects/${id}/chat/approve`)),
    retry: () => call(() => api.post(`/admin/studio/projects/${id}/chat/retry`)),
    stop: () => call(() => api.post(`/admin/studio/projects/${id}/chat/stop`)),
    rollback: (n) => call(async () => { await api.post(`/admin/studio/projects/${id}/chat/rollback/${n}`); toast.success(`Restored v${n}`); setDrawer(false); loadDetail(); loadQuote(); }),
  };
  const reloadAll = () => { loadChat(); loadDetail(); loadQuote(); };
  if (!chat) return <div className="fixed inset-0 z-40 grid place-items-center bg-[#07090E]"><Loader2 className="h-6 w-6 animate-spin text-cyan-300" /></div>;
  const chatEl = <Chat pid={id} chat={chat} running={running} act={act} composerRef={composerRef} />;
  const right = <RightPane tab={tab} setTab={setTab} chat={chat} detail={detail} preview={preview} running={running} reload={reloadAll} />;
  return (
    <div data-testid="studio-workspace" className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-[#07090E] text-slate-200">
      <TopBar chat={chat} detail={detail} quote={quote} setQuote={setQuote} running={running} setDrawer={setDrawer} setPub={setPub} act={act} />
      {narrow ? (
        <>
          <div className="flex shrink-0 border-b border-white/5">
            {[["chat", "Chat", MessageSquare], ["right", "Preview & code", Eye]].map(([k, l, I]) => (
              <button key={k} onClick={() => setMobileTab(k)} data-testid={`mobile-tab-${k}`} className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs ${mobileTab === k ? "border-b-2 border-cyan-400 text-white" : "text-slate-400"}`}><I className="h-3.5 w-3.5" />{l}</button>
            ))}
          </div>
          <div className="min-h-0 flex-1">{mobileTab === "chat" ? chatEl : right}</div>
        </>
      ) : (
        <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1">
          <ResizablePanel defaultSize={36} minSize={24} maxSize={60} data-testid="chat-panel">{chatEl}</ResizablePanel>
          <ResizableHandle withHandle data-testid="ws-resize-handle" className="bg-white/5" />
          <ResizablePanel defaultSize={64} minSize={35}>{right}</ResizablePanel>
        </ResizablePanelGroup>
      )}
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent data-testid="ws-history-drawer" className="w-full overflow-y-auto border-white/10 bg-[#0B0F18] sm:max-w-md">
          <SheetHeader><SheetTitle className="text-white">Version history</SheetTitle></SheetHeader>
          {detail && <div className="mt-4"><VersionsPanel project={chat.project} versions={detail.versions} running={running} onRestore={act.rollback} /></div>}
        </SheetContent>
      </Sheet>
      <Dialog open={!!pub} onOpenChange={(o) => !o && setPub(null)}>
        <DialogContent data-testid="ws-publish-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#0B0F18] text-white">
          <DialogHeader><DialogTitle>{pub === "20i" ? "Publish to 20i" : "Publish to my site"}</DialogTitle></DialogHeader>
          {pub === "site" && detail && <PublishPanel project={detail.project} versions={detail.versions} onChange={reloadAll} />}
          {pub === "20i" && detail && <TwentyIPublish projectId={id} versions={[...detail.versions].sort((a, b) => a.number - b.number)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
